import React, { useEffect, useState, useMemo } from "react";
import { PlannedPhase } from "../../pages/Room";
import PhaseLinearNode from "./PhaseLinearNode";
import "./PhaseLinearTimeline.css";
import { Room } from "../../config/parsedConfig";
import { useTheme } from "@emotion/react";
import { getThemeColor, getWhiteOrBlackForContrast } from "../../utils/color";

const TRANSITION_TIME = 2;   // Sekunden
// const NODE_DIAMETER = 64;    // = 2 * LARGE
const NODE_GAP = 104;   // Node-Durchmesser + gewünschter Abstand

interface PhaseLinearTimelineProps {
    inside?: boolean;
    isPlayingAudio?: boolean;
    pastPhases: PlannedPhase[];
    upcomingPhases: PlannedPhase[];
    room: Room;
}

export const PhaseLinearTimeline: React.FC<PhaseLinearTimelineProps> = ({
    inside,
    isPlayingAudio,
    pastPhases,
    upcomingPhases,
    room
}) => {
    const [triggerTransition, setTriggerTransition] = useState(0); // 0: kein Trigger, ansonsten: index der Phase, die "incoming" ist
    const [transitionProgress, setTransitionProgress] = useState(0);

    const allPhases = useMemo(
        () => [...pastPhases, ...upcomingPhases],
        [pastPhases, upcomingPhases]
    );
    const activeIdx = Math.min(pastPhases.length, allPhases.length - 1);
    const current = allPhases[activeIdx];
    const remaining = current.remainingTime ?? current.duration;

    const theme = useTheme() as any;
    const roomColor = getThemeColor(theme, room.color || "orange");
    const roomColorSecondary = getThemeColor(theme, room.colorSecondary || "grey");
    const roomColorTertiary = getThemeColor(theme, room.colorTertiary || "lightgrey");
    const colorText = getWhiteOrBlackForContrast(roomColorTertiary);

    // Reset bei Phase-Wechsel
    useEffect(() => {
        setTriggerTransition(0);
        setTransitionProgress(0);
    }, [activeIdx]);

    // Trigger für die 2 s-Transition
    useEffect(() => {
        if (remaining <= TRANSITION_TIME && !triggerTransition) {
            setTriggerTransition(activeIdx + 1);
            const start = performance.now();
            const step = (now: number) => {
                const passed = (now - start) / 1000;
                if (passed < TRANSITION_TIME) {
                    setTransitionProgress(Math.min(1, passed / TRANSITION_TIME));
                    requestAnimationFrame(step);
                } else {
                    setTransitionProgress(1);
                }
            };
            requestAnimationFrame(step);
        }
        //do not trigger useEffect if activeIdx changes, only when remaining time changes
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remaining, triggerTransition]);

    // *** Neuer: statische Breite bis activeIdx, erst in Transition interpolieren ***
    const fillWidth = useMemo(() => {
        const base = activeIdx * NODE_GAP;
        if (!triggerTransition) {
            return base;
        }
        // nach der letzen Phase sollte die Progress-Bar nicht weiter wachsen,
        if (activeIdx >= allPhases.length - 1) {
            return base;
        }
        // in den letzten TRANSITION_TIME Sekunden weich zum nächsten Node
        return base + transitionProgress * NODE_GAP;
    }, [activeIdx, triggerTransition, transitionProgress, allPhases.length]);

    // Bar bis Mitte letzte Bubble
    const fullWidth = useMemo(() => {
        return (allPhases.length - 1) * NODE_GAP;
    }, [allPhases.length]);

    return (
        <div
            className="phase-linear-timeline"
            style={{
                "--bar-color": roomColor,
                "--bar-bg": roomColorTertiary,
                "--bar-border": roomColorSecondary,
                "color": colorText,
            } as React.CSSProperties}
        >
            {/* Hintergrund-Bar */}
            <div className="progress-bg-border" style={{ width: fullWidth, left: NODE_GAP / 2 }} />
            <div className="progress-bg-inner" style={{ width: fullWidth, left: NODE_GAP / 2 }} />

            {/* Gefüllter Bereich */}
            <div className="progress-fill" style={{ width: fillWidth, left: NODE_GAP / 2 }} />

            {/* Nodes */}
            {allPhases.map((phase, idx) => {
                // const isPast = idx < pastPhases.length;
                const isActive = idx === activeIdx;
                const isIncoming = idx > 0 && idx !== activeIdx && idx === triggerTransition;
                return (
                    <PhaseLinearNode
                        key={`${phase.name}-${idx}`}
                        name={phase.name}
                        duration={phase.duration}
                        remainingTime={phase.remainingTime ?? phase.duration}
                        elapsedTime={phase.elapsedTime ?? 0}
                        isActive={isActive}
                        phaseType={phase.type}
                        isIncomingTransition={isIncoming}
                        transitionProgress={
                            isActive ?
                                (idx === triggerTransition 
                                    ? 0 //isActive switches to next index earlier than the tiggerTransition index. We must "hide" the transition progress in this frame!
                                    : transitionProgress)
                                : isIncoming ? transitionProgress
                                    : 0}
                        barColor={roomColor}
                        borderColor={roomColorSecondary}
                        bgColor={roomColorTertiary}
                        inside={inside}
                        roomColor={room.color || "primary"}
                        isPlayingAudio={isPlayingAudio}
                    />
                );
            })}
        </div>
    );
};

export default PhaseLinearTimeline;
